import React, { useCallback, useState } from 'react'
import FormWrapper from 'core/components/FormWrapper'
import Wizard from 'core/components/wizard/Wizard'
import useReactRouter from 'use-react-router'
import { routes } from 'core/utils/routes'
import { CloudProviders } from 'app/plugins/infrastructure/components/cloudProviders/model'
import DocumentMeta from 'core/components/DocumentMeta'
import WizardStep from 'core/components/wizard/WizardStep'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import PicklistField from 'core/components/validatedForm/DropdownField'
import CloudProviderPicklist from 'k8s/components/common/CloudProviderPicklist'
import ValidatedForm from 'core/components/validatedForm/ValidatedForm'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import ReviewClustersTable from './ReviewClustersTable'
import { registerExternalClusters } from './actions'
import { trackEvent } from 'utils/tracking'
import { ClusterCloudPlatforms } from 'app/constants'
import Bugsnag from 'utils/bugsnag'
import RegionsAndClustersSelection from './regions-and-clusters-selection'
import { memoize } from 'utils/misc'
import { generateRegionSummaryFields } from './helpers'
import EcoWarning from './EcoWarning'
import useListAction from 'core/hooks/useListAction'
import { listCloudProviders } from 'app/plugins/infrastructure/components/cloudProviders/new-actions'
import { useSelector } from 'react-redux'
import { cloudProvidersSelector } from 'app/plugins/infrastructure/components/cloudProviders/selectors'
import { listImportedClusters } from 'app/plugins/infrastructure/components/importedClusters/new-actions'

const useStyles = makeStyles<Theme>((theme) => ({
  validatedFormContainer: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  regionsAndClusters: {
    display: 'flex',
  },
  regions: {
    minWidth: 200,
  },
  clusters: {
    flexGrow: 1,
  },
}))

const wizardMetaFormattedNames = {
  name: 'GKE Provider',
}

const wizardMetaCalloutFields = Object.keys(wizardMetaFormattedNames)
const title = 'Import GKE Clusters'
const initialContext = {
  cloudProviderId: '',
  regions: [],
  selectedClusters: {},
  finalSelectedClusters: [],
}
const ImportGKEClusterPage = () => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { message: loadingCloudProvdersMessage, loading: loadingCloudProviders } = useListAction(
    listCloudProviders,
  )
  const cloudProviders = useSelector(cloudProvidersSelector)
  const { reload: reloadImportedClusters } = useListAction(listImportedClusters)
  const providerType = CloudProviders.Gcp
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (data) => {
    setSubmitting(true)
    Bugsnag.leaveBreadcrumb('Attempting to import GKE clusters', data)
    trackEvent('Import GKE Clusters', {
      cloud_provider_id: data.cloudProviderId,
      regions: data.regions,
      clusters: data.finalSelectedClusters.map((cluster) => cluster.id),
    })
    await registerExternalClusters({
      cloudProviderId: data.cloudProviderId,
      clusters: data.finalSelectedClusters,
      stack: ClusterCloudPlatforms.GKE,
      detailsKey: 'zone',
    })
    setSubmitting(false)
    // do this here bc invalidateCache in the actions doesn't seem to work
    reloadImportedClusters(true)
    history.push(routes.cluster.imported.list.path())
  }

  const getCloudProviderName = useCallback(
    memoize((id) => {
      return cloudProviders.find((cp) => cp.uuid === id)?.name
    }),
    [cloudProviders],
  )

  return (
    <FormWrapper
      title={title}
      backUrl={routes.cluster.imported.list.path()}
      loading={submitting || loadingCloudProviders}
      message={loadingCloudProvdersMessage}
    >
      <DocumentMeta title={title} bodyClasses={['form-view']} />
      <Wizard
        onComplete={handleSubmit}
        context={initialContext}
        originPath={routes.cluster.import.root.path()}
        submitLabel="Import"
      >
        {({ wizardContext, setWizardContext, onNext }) => {
          const { regionFormattedNames, regionCalloutFields } = generateRegionSummaryFields(
            wizardContext.selectedClusters,
          )
          return (
            <>
              <WizardStep
                stepId="selectProvider"
                label="Choose a Provider"
                summaryIcon={providerType}
                summaryTitle={title}
                summaryKeyOverrides={wizardMetaFormattedNames}
                summaryCalloutFields={wizardMetaCalloutFields}
              >
                <ValidatedForm
                  classes={{ root: classes.validatedFormContainer }}
                  initialValues={wizardContext}
                  onSubmit={setWizardContext}
                  triggerSubmit={onNext}
                  elevated={false}
                >
                  <FormFieldSection title="Select GKE Provider">
                    <PicklistField
                      DropdownComponent={CloudProviderPicklist}
                      id="cloudProviderId"
                      label="GKE Provider"
                      tooltip="Find GKE clusters to import from the selected cloud provider."
                      type={CloudProviders.Gcp}
                      onChange={(value) =>
                        setWizardContext({
                          cloudProviderId: value,
                          name: getCloudProviderName(value),
                        })
                      }
                      value={wizardContext.cloudProviderId}
                      required
                    />
                  </FormFieldSection>
                </ValidatedForm>
              </WizardStep>
              <WizardStep
                stepId="selectClusters"
                label="Choose Regions & Clusters"
                summaryKeyOverrides={regionFormattedNames}
                summaryCalloutFields={regionCalloutFields}
              >
                <RegionsAndClustersSelection
                  providerType={ClusterCloudPlatforms.GKE}
                  wizardContext={wizardContext}
                  setWizardContext={setWizardContext}
                  onNext={onNext}
                />
              </WizardStep>
              <WizardStep stepId="confirm" label="Review & Confirm">
                <ValidatedForm
                  classes={{ root: classes.validatedFormContainer }}
                  initialValues={wizardContext}
                  onSubmit={setWizardContext}
                  triggerSubmit={onNext}
                  elevated={false}
                >
                  <FormFieldSection title="Clusters to Import">
                    <EcoWarning />
                    <ReviewClustersTable
                      selectedClusters={wizardContext.selectedClusters}
                      onChange={(value) => setWizardContext({ finalSelectedClusters: value })}
                      value={wizardContext.finalSelectedClusters}
                      stack={ClusterCloudPlatforms.GKE}
                      required
                    />
                  </FormFieldSection>
                </ValidatedForm>
              </WizardStep>
            </>
          )
        }}
      </Wizard>
    </FormWrapper>
  )
}

export default ImportGKEClusterPage
